import { ALGOLIA_SEARCH_PAGE_CONFIG } from '../algolia/client';

const DEFAULT_CONFIG = {
  indexName: ALGOLIA_SEARCH_PAGE_CONFIG.indexName,
  queryParameter: 'q',
  placeholder: 'Search products',
  hitsPerPage: 24,
  documentTitle: 'Search results',
  filters: ALGOLIA_SEARCH_PAGE_CONFIG.filters || '',
  sortBy: ALGOLIA_SEARCH_PAGE_CONFIG.sortBy,
  attributes: {
    productType: 'product_type',
    type: 'tags',
    color: 'options.color',
    size: 'options.size',
    availability: 'inventory_available',
    price: 'price',
  },
  labels: {
    productType: 'Category',
    type: 'Type',
    color: 'Color',
    size: 'Size',
    availability: 'Availability',
    availabilityOption: 'In stock only',
    price: 'Price',
  },
};

function removeBlankValues(input) {
  if (Array.isArray(input)) {
    return input
      .map((item) => removeBlankValues(item))
      .filter((item) => item !== undefined);
  }

  if (!input || typeof input !== 'object') {
    if (input === '' || input == null) {
      return undefined;
    }

    return input;
  }

  return Object.keys(input).reduce((accumulator, key) => {
    const value = removeBlankValues(input[key]);

    if (value !== undefined) {
      accumulator[key] = value;
    }

    return accumulator;
  }, {});
}

function parseColorSwatchMap(root) {
  const node = root.querySelector('[data-color-swatch-map]');

  if (!node) {
    return {};
  }

  try {
    const raw = JSON.parse(node.textContent);
    const normalized = {};

    for (const [key, value] of Object.entries(raw)) {
      normalized[key.toLowerCase().trim()] = value;
    }

    return normalized;
  } catch (error) {
    console.error('Unable to parse color swatch map JSON.', error);
    return {};
  }
}

function parseProductBadgeMap(root) {
  const node = root.querySelector('[data-product-badge-map]');

  if (!node) {
    return {};
  }

  try {
    const raw = JSON.parse(node.textContent);
    const entries = Array.isArray(raw) ? raw : Object.values(raw);

    return entries.reduce((map, badge) => {
      if (badge && badge.id != null && badge.text) {
        map[String(badge.id)] = {
          text: badge.text,
          background_color: badge.background_color || null,
          text_color: badge.text_color || null,
        };
      }

      return map;
    }, {});
  } catch (error) {
    console.error('Unable to parse product badge map JSON.', error);
    return {};
  }
}

function parseNoResultsConfig(root) {
  const node = root.querySelector('[data-no-results-config]');

  if (!node) {
    return { heading: 'No products found', subtext: '', collections: [] };
  }

  try {
    const raw = JSON.parse(node.textContent);

    return {
      heading: raw.heading || 'No products found',
      subtext: raw.subtext || '',
      collections: Array.isArray(raw.collections) ? raw.collections.filter((c) => c && c.url) : [],
    };
  } catch (error) {
    console.error('Unable to parse no-results config JSON.', error);
    return { heading: 'No products found', subtext: '', collections: [] };
  }
}

function parseJsonConfig(root) {
  const configNode = root.querySelector('[data-instant-search-config]');

  if (!configNode) {
    return {};
  }

  try {
    return JSON.parse(configNode.textContent);
  } catch (error) {
    console.error('Unable to parse InstantSearch config JSON.', error);
    return {};
  }
}

function getGlobalConfig() {
  if (window.theme && window.theme.algoliaSearchPage) {
    return window.theme.algoliaSearchPage;
  }

  if (window.theme && window.theme.algolia && window.theme.algolia.searchPage) {
    return window.theme.algolia.searchPage;
  }

  if (window.algoliaSearchPage) {
    return window.algoliaSearchPage;
  }

  return {};
}

function normalizeSortItems(config) {
  const sortItems = Array.isArray(config.sortBy) ? config.sortBy : [];
  const normalizedItems = sortItems.filter((item) => item && item.label && item.value);

  if (normalizedItems.length) {
    return normalizedItems;
  }

  return [
    {
      label: 'Featured',
      value: config.indexName,
    },
  ];
}

export function getSearchConfig(root) {
  const sectionConfig = removeBlankValues(parseJsonConfig(root));
  const globalConfig = removeBlankValues(getGlobalConfig());
  const mergedConfig = {
    ...DEFAULT_CONFIG,
    ...globalConfig,
    ...sectionConfig,
    attributes: {
      ...DEFAULT_CONFIG.attributes,
      ...globalConfig.attributes,
      ...sectionConfig.attributes,
    },
    labels: {
      ...DEFAULT_CONFIG.labels,
      ...globalConfig.labels,
      ...sectionConfig.labels,
    },
  };

  const baseFilters = DEFAULT_CONFIG.filters;
  const extraFilters = mergedConfig.filters !== baseFilters ? mergedConfig.filters : '';
  const filters = [baseFilters, extraFilters].filter(Boolean).join(' AND ');

  return {
    ...mergedConfig,
    indexName: DEFAULT_CONFIG.indexName,
    hitsPerPage: Number(mergedConfig.hitsPerPage) || DEFAULT_CONFIG.hitsPerPage,
    filters,
    sortBy: normalizeSortItems(mergedConfig),
    colorSwatchMap: parseColorSwatchMap(root),
    productBadgeMap: parseProductBadgeMap(root),
    noResults: parseNoResultsConfig(root),
  };
}

export function hasRequiredConfig(config) {
  return Boolean(config.indexName);
}
